import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2, Share2, MessageCircle, Copy, Twitter } from "lucide-react";
import confetti from "canvas-confetti";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAssessment } from "@/context/AssessmentContext";
import { useGeo } from "@/lib/geo";
import { toast } from "sonner";

const SITE_URL = "https://apexture.com";

// Fire a rich, multi-burst confetti sequence — feels like a genuine celebration
function fireConfetti() {
  const forestPalette = ["#1B4332", "#95D5B2", "#F4A261", "#E9C46A", "#F5F1E8"];
  const burst = (originX) => {
    confetti({
      particleCount: 90,
      spread: 80,
      startVelocity: 45,
      origin: { x: originX, y: 0.35 },
      colors: forestPalette,
      scalar: 1.05,
      ticks: 220,
    });
  };
  // Two side bursts + a big top burst
  burst(0.2);
  burst(0.8);
  setTimeout(() => {
    confetti({
      particleCount: 160,
      spread: 120,
      startVelocity: 55,
      origin: { x: 0.5, y: 0.15 },
      colors: forestPalette,
      scalar: 1.15,
      ticks: 260,
    });
  }, 250);
  // A gentle after-shower
  setTimeout(() => {
    confetti({
      particleCount: 60,
      spread: 100,
      startVelocity: 30,
      origin: { x: 0.5, y: 0.05 },
      colors: forestPalette,
      scalar: 0.9,
      gravity: 0.7,
    });
  }, 900);
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("CHECKING");
  const { refresh, user } = useAuth();
  const { data } = useAssessment();
  const { isIndia } = useGeo();
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!orderId) { setStatus("MISSING"); return; }
    let tries = 0;
    const timer = setInterval(async () => {
      try {
        const { data } = await http.get(`/payments/status/${orderId}`);
        setStatus(data.status);
        if (data.status === "PAID") {
          clearInterval(timer);
          await refresh();
        }
      } catch {}
      if (++tries >= 15) clearInterval(timer);
    }, 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [orderId]);

  const paid = status === "PAID";
  const failed = ["FAILED", "EXPIRED", "TERMINATED"].includes(status);

  // Fire confetti exactly once when paid
  useEffect(() => {
    if (paid && !firedRef.current) {
      firedRef.current = true;
      fireConfetti();
    }
  }, [paid]);

  // Compose a shareable message using the user's posture score if available
  const score = data?.report?.posture_score;
  const patternName = data?.pattern?.plain_name || data?.pattern?.name;
  const priceLabel = isIndia ? "₹199" : "$2";
  const shareText = score
    ? `I just got a ${score}/100 posture score on Apexture — an AI-powered posture check with a 3-month fix plan. Try it (${priceLabel}) 👉 ${SITE_URL}`
    : `I just unlocked my personalised posture plan on Apexture — AI-powered check + 3-month home fix routine. Try it (${priceLabel}) 👉 ${SITE_URL}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Apexture Posture Score",
          text: shareText,
          url: SITE_URL,
        });
      } catch {/* user cancelled */}
    } else {
      copyLink();
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied — paste it anywhere!");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="max-w-lg mx-auto py-12 text-center animate-fade-in">
      <Card className="p-8 sm:p-10 bg-white border-forest/10 overflow-hidden relative">
        {status === "CHECKING" && (
          <>
            <Loader2 className="w-14 h-14 text-forest animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-forest mb-2">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}

        {paid && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-sage/20 flex items-center justify-center mb-4 animate-bounce-slow">
              <CheckCircle2 className="w-14 h-14 text-sage" strokeWidth={2} />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-forest mb-2 tracking-tight">
              You're in! 🎉
            </h1>
            <p className="text-base text-muted-foreground mb-6">
              3-month full access is now active. Time to fix that {patternName ? patternName.toLowerCase() : "posture"}.
            </p>

            {score != null && (
              <div className="bg-bone border border-forest/10 rounded-xl p-5 mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-sage mb-1">Your posture score</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-heading text-6xl font-bold text-forest leading-none">{score}</span>
                  <span className="text-muted-foreground text-lg">/100</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Improve it over the next 12 weeks with your plan.</p>
              </div>
            )}

            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-forest mb-3">Share the fix with a friend</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  onClick={nativeShare}
                  variant="outline"
                  className="border-forest/20 hover:bg-forest/5"
                  data-testid="share-native-btn"
                >
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-forest/20 hover:bg-forest/5"
                  data-testid="share-whatsapp-btn"
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-forest/20 hover:bg-forest/5"
                  data-testid="share-twitter-btn"
                >
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                    <Twitter className="w-4 h-4 mr-1" /> Twitter
                  </a>
                </Button>
                <Button
                  onClick={copyLink}
                  variant="outline"
                  className="border-forest/20 hover:bg-forest/5"
                  data-testid="share-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={() => navigate("/report")}
                className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7"
                data-testid="see-report-btn"
              >
                See my full report →
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-forest hover:bg-forest/5 rounded-full"
              >
                Back to home
              </Button>
            </div>
          </>
        )}

        {failed && (
          <>
            <XCircle className="w-14 h-14 text-terracotta mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-forest mb-2">Payment didn't go through</h1>
            <p className="text-sm text-muted-foreground mb-6">No charge was made. Try again anytime.</p>
            <Button asChild className="bg-forest hover:bg-forest/90 text-bone">
              <Link to="/pricing">Retry</Link>
            </Button>
          </>
        )}

        {status === "MISSING" && (
          <>
            <XCircle className="w-14 h-14 text-terracotta mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-forest mb-2">Order not found</h1>
            <p className="text-sm text-muted-foreground mb-6">If you were charged, please contact support.</p>
            <Button asChild variant="outline">
              <Link to="/pricing">Back to Pricing</Link>
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
